/**
 * Scroll engine for the Cue + Conductor hero.
 *
 * The interaction model follows the approved behavioural reference: a long
 * scroll track, a sticky viewport-height stage, and normalised progress that
 * every visual state is derived from. There is no timeline, no autoplay and no
 * animation history — given a progress value the rendered state is identical
 * however that value was reached, so reversing scroll reverses the visuals
 * exactly. Scrolling is never intercepted: no wheel or touch handler, no
 * preventDefault, no synthetic smoothing.
 *
 * Per frame this reads one rect and then only writes custom properties, so the
 * browser does a single layout pass. Geometry that cannot change without a
 * resize is cached.
 */

import {
  FLAT_CUES,
  SEQUENCE_END,
  characterCadence,
  counterText,
  cueValue,
  internalProgress,
  typingState,
  typingStateAtScroll,
  type FlatCue,
  type TypingState,
} from './internalTimeline';
import {
  PHASES,
  SCENE_SEQUENCE,
  abState,
  backgroundState,
  clamp,
  dominantScene,
  easeInOutCubic,
  lerp,
  sceneOpacity,
  sceneQuiet,
  sceneScale,
  sceneTravel,
  segment,
  titleOpacity,
  titleTravel,
  type SceneId,
} from './timeline';
import { ShowcaseV3Engine } from './v3/engine';
import { ShowcaseV4Engine } from './v4/engine';
import { mountV4MobileStage } from './v4/mobileStage';

const REDUCED_MOTION = '(prefers-reduced-motion: reduce)';

interface SceneRefs {
  id: SceneId;
  el: HTMLElement;
  /** Cues belonging to this scene, resolved once at construction. */
  cues: FlatCue[];
  /** Every property this scene's cues write, for a one-shot clear. */
  props: string[];
  /** Whether the scene's internals are currently written. */
  live: boolean;
}

interface CounterRef {
  el: HTMLElement;
  /** Markup value, restored whenever the engine stops driving the number. */
  final: string;
}

// Breathing room kept below the fitted Prompt panel so it never kisses the stage edge.
const PROMPT_FIT_GAP = 24;

class HeroEngine {
  private readonly root: HTMLElement;
  private readonly track: HTMLElement;
  private readonly stage: HTMLElement;
  private readonly scenes: SceneRefs[];

  /** Scroll distance the track offers once the stage height is removed. */
  private distance = 1;
  private frame = 0;
  private queued = false;
  private engaged = false;
  private lastWritten = new Map<string, string>();
  private lastActive: SceneId | null = null;
  /**
   * Whether the stylesheet is currently pinning the stage. Short landscape and
   * any other unpinned layout shows the scenes as a linear stack, where taking
   * five of six out of the a11y tree would hide visible content.
   */
  private pinned = true;

  constructor(root: HTMLElement) {
    this.root = root;
    this.track = root.querySelector<HTMLElement>('[data-track]')!;
    this.stage = root.querySelector<HTMLElement>('[data-stage]')!;
    this.scenes = SCENE_SEQUENCE.map((id) => ({
      id,
      el: root.querySelector<HTMLElement>(`[data-scene="${id}"]`)!,
      cues: FLAT_CUES.filter((cue) => cue.scene === id),
      // The typed sentence is deliberately absent: it is latched once written,
      // so leaving and re-entering the Prompt scene keeps the finished sentence.
      props: [
        `--l-${id}`,
        ...FLAT_CUES.filter((cue) => cue.scene === id && cue.prop).map((cue) => cue.prop),
      ],
      live: false,
    })).filter((s) => s.el);

    // Every DOM reference the render loop needs, resolved once.
    const typed = root.querySelector<HTMLElement>('[data-layer-inner="typing"]');
    this.cadence = characterCadence(Number(typed?.dataset.length ?? 0));
    this.greeting = root.querySelector<HTMLElement>('[data-layer="greeting"]');
    this.intro = root.querySelector<HTMLElement>('[data-hcc-intro]');
    this.header = document.querySelector<HTMLElement>('header.site-header');
    this.panel = root.querySelector<HTMLElement>('[data-scene="prompt"] [data-layer="panel"]');
    this.promptTransform = root.querySelector<HTMLElement>(
      '[data-scene="prompt"] [data-scene-transform]',
    );
    this.cta = root.querySelector<HTMLElement>('[data-hcc-cta]');

    for (const cue of FLAT_CUES) {
      if (!cue.countKey) {
        continue;
      }
      const el = root.querySelector<HTMLElement>(`[data-count="${cue.countKey}"]`);
      if (el) {
        this.counters.set(cue.countKey, { el, final: el.textContent ?? '' });
      }
    }
  }

  /** Cached geometry. Called on init and on every resize, never per frame. */
  private measure = () => {
    const stageHeight = this.stage.offsetHeight || window.innerHeight;
    this.distance = Math.max(1, this.track.offsetHeight - stageHeight);

    /*
     * Where each scene's background composition sits. The block paints one
     * repeating Momentum tile; a scene's anchor is the tile offset the stage
     * would naturally show at that scene's anchor progress, so every held
     * gradient is a genuine slice of the approved composition and the two ends
     * line up with the hero intro above and the release band below.
     */
    const style = getComputedStyle(this.root);
    const base = this.track.offsetTop;

    /*
     * Opening hand-off.
     *
     * The panel settles at --hcc-prompt-top once the stage is pinned, but at
     * the top of the page that would put it far below the fold, leaving a large
     * gap under the CTA and no readable greeting. So the panel starts lifted by
     * `preroll` and eases to its settle position exactly as the stage pins.
     * The lift is derived from the *measured* bottom of the greeting rather
     * than a fixed number of panel pixels, so the opening frame shows the whole
     * greeting plus a set amount of breathing room at any viewport size.
     *
     * It is clamped at or below zero, so the panel is only ever lifted, never
     * pushed down, and the mapping is a straight function of how far the track
     * has travelled — no jump when sticky positioning takes over, and it
     * unwinds identically on the way back up.
     */
    // Read off the element that consumes it: below 980px the custom property is a clamp() that parseFloat cannot resolve.
    this.promptTop = this.promptTransform
      ? parseFloat(getComputedStyle(this.promptTransform).paddingTop) || 0
      : 0;

    this.preroll = 0;
    if (
      parseFloat(style.getPropertyValue('--hcc-open-handoff')) === 1 &&
      this.greeting &&
      this.panel
    ) {
      const breathing = parseFloat(style.getPropertyValue('--hcc-breathing')) || 32;
      const promptTop = this.promptTop;
      const greetingBottom = Math.round(
        this.greeting.getBoundingClientRect().bottom - this.panel.getBoundingClientRect().top,
      );
      // Publish the measurement so the intro can size itself from it too.
      this.root.style.setProperty('--hcc-greeting-h', `${greetingBottom}px`);
      const desired = window.innerHeight - breathing - greetingBottom;
      this.preroll = Math.min(0, desired - (base + promptTop));
    } else {
      this.root.style.removeProperty('--hcc-greeting-h');
    }
    this.trackTop = Math.max(1, base);

    /*
     * Boundaries for the CTA's vertical centring. The CTA is centred in the
     * *actual* gap above the Prompt panel, which is bounded above by the hero
     * copy while that is still on screen and by the usable top of the stage
     * once it is not. `chromeTop` is read from the document's own rendered
     * margin, which is 0 in production and picks up the admin bar's offset
     * during logged-in QA without any admin-specific rule.
     */
    this.centreCta = parseFloat(style.getPropertyValue('--hcc-cta-centre')) === 1;
    if (this.centreCta && this.cta && this.intro) {
      /*
       * Layout offsets, not rendered rects: this element carries the very
       * translate being computed from it, so measuring its painted position
       * would feed back into itself and settle at the wrong place.
       */
      const heroTop = Math.round(this.root.getBoundingClientRect().top + window.scrollY);
      this.copyBottom = heroTop + this.intro.offsetTop + this.intro.offsetHeight;
      // Derived from the row above plus its own margin: offsetTop on a stuck sticky element reports the shifted offset, not the layout one.
      const ctaMargin = parseFloat(getComputedStyle(this.cta).marginTop) || 0;
      this.ctaNaturalTop = this.copyBottom + ctaMargin;
      this.ctaHeight = this.cta.offsetHeight;
      this.chromeTop = this.topChrome();
      this.ctaMinGap = parseFloat(style.getPropertyValue('--hcc-cta-min-gap')) || 16;
    }

    this.publishChrome();

    this.heroBottom =
      Math.round(this.root.getBoundingClientRect().top + window.scrollY) + this.root.offsetHeight;

    /*
     * Short-desktop fit. The panel's stage-relative footprint is fixed above
     * 980px, so a wide-but-short viewport leaves less stage than it needs. This
     * is the shrink factor that makes it fit; the panel's own top is unchanged,
     * so the CTA keeps its relationship to it. offsetHeight is the layout
     * height, unaffected by the scale it drives, so there is no feedback loop.
     */
    if (this.panel) {
      const available = this.stage.offsetHeight - this.promptTop - PROMPT_FIT_GAP;
      const needed = this.panel.offsetHeight;
      const fit = needed > 0 ? Math.min(1, available / needed) : 1;
      this.writeRoot('--hcc-prompt-fit', fit.toFixed(4));
    }

    // One computed-style read per resize, never per frame.
    const wasPinned = this.pinned;
    this.pinned = getComputedStyle(this.stage).position === 'sticky';

    if (wasPinned && !this.pinned) {
      this.resetSceneState();
      this.clearInternals();
    }
  };

  /** Hand every scene back to the linear layout: visible, focusable, announced. */
  private resetSceneState() {
    this.lastActive = null;
    for (const { el } of this.scenes) {
      el.removeAttribute('data-scene-active');
      el.inert = false;
      el.removeAttribute('aria-hidden');
    }
  }

  /**
   * Drop one scene's internal properties so its layers fall back to the value
   * the stylesheet defaults them to. Called both when a scene leaves the frame
   * and when the whole engine stands down.
   */
  private clearScene(scene: SceneRefs) {
    if (!scene.live) {
      return;
    }
    scene.live = false;
    for (const name of scene.props) {
      this.stage.style.removeProperty(name);
      this.lastWritten.delete(name);
    }
    for (const cue of scene.cues) {
      const counter = cue.countKey ? this.counters.get(cue.countKey) : undefined;
      if (counter && counter.el.textContent !== counter.final) {
        counter.el.textContent = counter.final;
      }
    }
  }

  /** Hand every internal layer back to its final state (the CSS fallback). */
  private clearInternals() {
    for (const scene of this.scenes) {
      this.clearScene(scene);
    }
  }

  /** Custom properties the CTA needs, which is not inside the stage. */
  private writeRoot(name: string, value: string) {
    if (this.lastWritten.get(name) === value) {
      return;
    }
    this.lastWritten.set(name, value);
    this.root.style.setProperty(name, value);
  }

  private write(name: string, value: string) {
    if (this.lastWritten.get(name) === value) {
      return;
    }
    this.lastWritten.set(name, value);
    this.stage.style.setProperty(name, value);
  }

  /** Pure render: progress in, custom properties out. */
  private render = () => {
    this.queued = false;

    // The single layout read of the frame.
    const top = this.track.getBoundingClientRect().top;
    const p = clamp(-top / this.distance);

    this.write('--p', p.toFixed(4));

    // Opening hand-off: unwinds as the track travels up to the viewport top.
    const qNow = clamp(1 - top / this.trackTop);
    if (this.preroll !== 0) {
      this.write('--hcc-preroll', `${(this.preroll * (1 - qNow)).toFixed(1)}px`);
    }

    /*
     * Background: two stationary full-viewport layers, cross-faded by opacity.
     * While a scene is established `from` and `to` are the same preset and the
     * mix is 0, so all three writes are change-detected away and the rendered
     * pixels are untouched. Only inside a transition window does one opacity
     * move; the gradient images are never interpolated or repositioned.
     */
    /*
     * Header ground. One state change at the hero's bottom edge rather than any
     * per-frame colour work: past it the overlay presentation is withdrawn and
     * the header falls back to its standard styling.
     */
    const pastHero = this.heroBottom - window.scrollY <= this.chromeTop;
    /*
     * Withdrawing the state is gated on boot being finished. The first frames of
     * a restored load run before the browser applies the scroll position, so
     * scrollY still reads 0 there and this would otherwise clear the pre-paint
     * state and then set it again a frame later - which is exactly the
     * plum-to-white animation the boundary transition must never play on load.
     */
    if (pastHero !== this.pastHero && (pastHero || this.root.dataset.hccBoot === 'ready')) {
      this.pastHero = pastHero;
      if (pastHero) {
        document.documentElement.dataset.hccHeader = 'standard';
      } else {
        delete document.documentElement.dataset.hccHeader;
      }
    }

    const bg = backgroundState(p, qNow);
    this.writeRoot('--hcc-bg-a', `var(--hcc-bg-${bg.from})`);
    this.writeRoot('--hcc-bg-b', `var(--hcc-bg-${bg.to})`);
    this.writeRoot('--hcc-bg-mix', bg.mix.toFixed(4));

    for (const { id } of this.scenes) {
      this.write(`--o-${id}`, sceneOpacity(id, p).toFixed(3));
      this.write(`--y-${id}`, sceneTravel(id, p).toFixed(3));
      this.write(`--s-${id}`, sceneScale(id, p).toFixed(4));
      this.write(`--to-${id}`, titleOpacity(id, p).toFixed(3));
      this.write(`--ty-${id}`, titleTravel(id, p).toFixed(3));
      this.write(`--ab-${id}`, abState(id, p).toFixed(3));
      this.write(`--q-${id}`, sceneQuiet(id, p).toFixed(3));
    }

    if (!this.pinned) {
      // Linear stack: every scene is visible, so it must be complete.
      return;
    }

    this.renderInternals(p);

    /*
     * Centre the CTA in whatever gap currently exists above the Prompt panel.
     * One expression covers both states and everything between them: while the
     * hero copy is on screen it is the upper boundary, and once it has scrolled
     * away the usable stage top takes over. Because both boundaries move
     * continuously with scroll, so does the CTA — there is no offset to jump
     * between and nothing is pinned to the viewport top.
     */
    if (this.centreCta && this.cta) {
      const scrollY = window.scrollY;
      // The stage is sticky, so its top stops at the viewport top rather than
      // continuing to travel with the page.
      const stageTop = Math.max(this.chromeTop, this.trackTop - scrollY);
      const lower = stageTop + this.promptTop + this.preroll * (1 - qNow);
      const upper = Math.max(this.copyBottom - scrollY, this.chromeTop);
      let desired = (upper + lower) / 2 - this.ctaHeight / 2;
      // Clear of the chrome above and the panel below, but only while the gap can
      // hold both margins: under that it stays centred rather than being pushed
      // off the top by the lower clamp.
      const floor = this.chromeTop + this.ctaMinGap;
      const ceiling = lower - this.ctaHeight - this.ctaMinGap;
      desired =
        ceiling >= floor
          ? Math.min(Math.max(desired, floor), ceiling)
          : Math.max(this.chromeTop, desired);
      const stickyTop = Math.max(0, this.ctaNaturalTop - scrollY);
      this.writeRoot('--hcc-cta-y', `${(desired - stickyTop).toFixed(1)}px`);
    }

    // The CTA carries into the Prompt scene and leaves with it, so it is taken
    // out of the tab order and out of the way once that scene has gone.
    if (this.cta) {
      const promptOpacity = sceneOpacity('prompt', p);
      // The CTA sits outside the stage, so it cannot inherit --o-prompt.
      this.writeRoot('--hcc-cta-o', promptOpacity.toFixed(3));
      const live = promptOpacity > 0.02;
      if (live !== this.ctaLive) {
        this.ctaLive = live;
        this.cta.inert = !live;
        this.cta.toggleAttribute('data-hcc-cta-off', !live);
      }
    }

    // The whole Prompt demonstration is a pure function of scroll: the sentence
    // from the pre-pin progress, the chips and "More prompts" from the scene's
    // own window. Recomputed every frame, so scrolling back up unwinds it.
    if (this.cadence.length > 0) {
      this.applyTyping(typingStateAtScroll(qNow, p, this.cadence));
    }

    // Pointer and assistive-tech ownership follows the frame's dominant scene.
    const active = dominantScene(p);
    if (active !== this.lastActive) {
      this.lastActive = active;
      for (const { id, el } of this.scenes) {
        const isActive = id === active;
        el.toggleAttribute('data-scene-active', isActive);
        // Fully invisible scenes leave the a11y tree and the tab order.
        el.inert = !isActive;
        el.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      }
    }
  };

  /**
   * Stage 2B: choreography inside the scenes. Only scenes with something on
   * screen are evaluated, and a scene that leaves has its properties removed —
   * so the set of written values at a given `p` is the same however that `p`
   * was reached, and no invisible scene holds a stale value.
   */
  private renderInternals(p: number) {
    for (const scene of this.scenes) {
      if (sceneOpacity(scene.id, p) <= 0) {
        // Off screen: drop its properties rather than leave the last values
        // behind, so the written state at a given `p` never depends on history.
        this.clearScene(scene);
        continue;
      }
      scene.live = true;

      const local = internalProgress(scene.id, p);
      this.write(`--l-${scene.id}`, local.toFixed(4));

      for (const cue of scene.cues) {
        if (cue.prop) {
          this.write(cue.prop, cueValue(cue, local).toFixed(3));
        }
        if (!cue.countKey) {
          continue;
        }
        const counter = this.counters.get(cue.countKey);
        const text = counterText(cue, local);
        // Change-detected: the accessible copy is separate and never touched.
        if (counter && counter.el.textContent !== text) {
          counter.el.textContent = text;
        }
      }
    }
  }

  private applyTyping(state: TypingState) {
    this.write('--chars-prompt', `${state.chars}`);
    this.write('--caret-prompt', state.caret.toFixed(3));
    // The Prompt interface reveals run on this clock, not on scroll, so the
    // whole demonstration completes without the user having to scroll again.
    for (let i = 0; i < state.controls.length; i += 1) {
      this.write(`--r-prompt-controls-${i}`, state.controls[i].toFixed(3));
    }
    for (let i = 0; i < state.chips.length; i += 1) {
      this.write(`--r-prompt-chips-${i}`, state.chips[i].toFixed(3));
    }
    this.write('--r-prompt-more', state.more.toFixed(3));
    // Hands the caret to the stylesheet's slow blink on the final character, and
    // takes it back when the sentence unwinds on the way up.
    if (state.sentenceDone) {
      if (!this.root.dataset.hccTyped) {
        this.root.dataset.hccTyped = 'on';
      }
    } else if (this.root.dataset.hccTyped) {
      delete this.root.dataset.hccTyped;
    }
  }

  private schedule = () => {
    if (this.queued) {
      return;
    }
    this.queued = true;
    this.frame = requestAnimationFrame(this.render);
  };

  private onResize = () => {
    this.measure();
    this.schedule();
  };

  engage() {
    if (this.engaged) {
      return;
    }
    this.engaged = true;

    this.measure();
    this.root.setAttribute('data-hcc-engine', 'on');
    // Own the pre-paint flag from here on, so a live change to the
    // prefers-reduced-motion setting switches the layout as well as the engine.
    document.documentElement.dataset.hccJs = 'on';

    // One passive scroll listener, one resize path, one rAF loop.
    window.addEventListener('scroll', this.schedule, { passive: true });
    window.addEventListener('resize', this.onResize);
    window.visualViewport?.addEventListener('resize', this.onResize);

    // Late webfont and image loads change the stage height.
    this.resizeObserver = new ResizeObserver(this.onResize);
    this.resizeObserver.observe(this.track);

    this.render();
    this.settle();

    // A BFCache return restores scroll without re-running the script.
    window.addEventListener('pageshow', this.onPageShow);
  }

  /**
   * Boot / restoration.
   *
   * A reload keeps the scroll position, but the browser restores it after the
   * document is laid out — so the first render can land at the old position and
   * the next one at the restored one, which is what made the CTA and the active
   * scene visibly fly into place. This waits for the restored position to stop
   * changing, applies the whole state in one render, and only then marks the
   * engine ready. Nothing animates from progress 0, and no scene is shown at a
   * progress the page is not actually at.
   */
  /**
   * Bottom of the sticky top chrome: the admin bar's own offset on the document
   * plus the header's rendered flow height. Measured rather than duplicated as
   * constants, so an absent announcement bar or a logged-in admin bar are both
   * handled without a second source of truth.
   */
  private topChrome() {
    const adminBar = parseFloat(getComputedStyle(document.documentElement).marginTop) || 0;
    return adminBar + (this.header ? this.header.offsetHeight : 0);
  }

  private publishChrome() {
    const headerH = this.header ? this.header.offsetHeight : 0;
    const style = document.documentElement.style;
    style.setProperty('--hcc-header-h', `${headerH}px`);
    style.setProperty('--hcc-chrome-offset', `${this.topChrome()}px`);
  }

  private settle = () => {
    let last = -1;
    let stable = 0;
    const step = () => {
      const y = Math.round(window.scrollY);
      stable = y === last ? stable + 1 : 0;
      last = y;
      if (stable < 2) {
        requestAnimationFrame(step);
        return;
      }
      this.measure();
      this.render();
      if (this.root.dataset.hccBoot !== 'ready') {
        this.root.dataset.hccBoot = 'ready';
      }

      // Arms the header's boundary crossfade. Until this is set, any state
      // correction while the restored position is still settling applies
      // instantly, so a load never animates from one header state to the other.
      document.documentElement.dataset.hccReady = '1';
    };
    requestAnimationFrame(step);
  };

  private onPageShow = (event: PageTransitionEvent) => {
    if (!event.persisted) {
      return;
    }
    // Restored from the back/forward cache: geometry is intact but the scroll
    // position may differ, so re-derive everything and re-settle.
    this.root.dataset.hccBoot = 'restoring';
    this.measure();
    this.render();
    this.settle();
  };

  release() {
    if (!this.engaged) {
      return;
    }
    this.engaged = false;

    cancelAnimationFrame(this.frame);
    window.removeEventListener('scroll', this.schedule);
    window.removeEventListener('pageshow', this.onPageShow);
    window.removeEventListener('resize', this.onResize);
    window.visualViewport?.removeEventListener('resize', this.onResize);
    this.resizeObserver?.disconnect();
    this.resizeObserver = undefined;

    this.root.removeAttribute('data-hcc-engine');
    delete this.root.dataset.hccBoot;
    document.documentElement.dataset.hccJs = 'reduced';
    // Reduced motion shows the whole sentence at once; drop the typed state so
    // the stylesheet fallback takes over, and allow a later re-engage to type.
    delete this.root.dataset.hccTyped;
    this.root.style.removeProperty('--hcc-greeting-h');
    this.root.style.removeProperty('--hcc-preroll');
    this.root.style.removeProperty('--hcc-cta-o');
    this.root.style.removeProperty('--hcc-cta-y');
    this.root.style.removeProperty('--hcc-bg-a');
    this.root.style.removeProperty('--hcc-bg-b');
    this.root.style.removeProperty('--hcc-bg-mix');
    if (this.cta) {
      this.cta.inert = false;
      this.cta.removeAttribute('data-hcc-cta-off');
      this.ctaLive = true;
    }
    this.clearInternals();
    this.lastWritten.forEach((_, name) => this.stage.style.removeProperty(name));
    this.lastWritten.clear();

    this.resetSceneState();
  }

  private resizeObserver?: ResizeObserver;
  private readonly counters = new Map<string, CounterRef>();
  private greeting: HTMLElement | null = null;
  private panel: HTMLElement | null = null;
  private cta: HTMLElement | null = null;
  private intro: HTMLElement | null = null;
  private ctaLive = true;
  private centreCta = false;
  private copyBottom = 0;
  private ctaNaturalTop = 0;
  private ctaHeight = 40;
  private header: HTMLElement | null = null;

  private heroBottom = 0;

  private pastHero: boolean | null =
    document.documentElement.dataset.hccHeader === 'standard' ? true : null;

  private chromeTop = 0;
  private ctaMinGap = 16;
  private promptTransform: HTMLElement | null = null;

  private promptTop = 0;
  private preroll = 0;
  private trackTop = 1;
  private cadence: number[] = [];
}

/**
 * Automatic showcase (V2) controller.
 *
 * Same visual system as the scroll engine — every scene state is a pure
 * function of progress `p`, written as the same custom properties the shared
 * stylesheet and scene components already consume — but `p` comes from an
 * elapsed-time clock rather than scroll. There is no track, no sticky stage, no
 * scroll listener, no preroll and no CTA carry: the page scrolls normally and
 * the product story plays inside a normal-flow showcase stage.
 *
 * The clock is two-phase so the shared PHASES distribution does not rush the
 * Prompt: the Prompt's time-based typing plays first at its designed cadence,
 * then `p` sweeps 0 -> 1 through the scene sequence. It plays once, pauses off
 * screen or on a hidden tab, resumes from the same elapsed time, and holds on
 * Reports at the end. Under prefers-reduced-motion it renders one settled poster
 * and never starts the clock.
 */
/**
 * V2-only pace map: milliseconds within the sweep -> shared progress `p`.
 *
 * A linear sweep spends real time in proportion to each scene's scroll-designed
 * `p` span, which left the Prompt and Desktop sitting settled for seconds while
 * the surface scenes flew past. This remaps elapsed to `p` so each scene's
 * assembly and cross-fade keep their original ~13000ms-per-unit rate (the Stage
 * 2C choreography is untouched) while the dead between-scene holds are
 * compressed to a short, even settle beat. Monotonic: time and `p` both rise.
 */
const SWEEP_PACE: ReadonlyArray<readonly [number, number]> = [
  [0, 0.0],
  [450, 0.1325], // prompt eased out; desktop fade begins
  [1808, 0.237], // desktop assembled
  [2658, 0.327], // desktop settle + A/B; mobile fade begins
  [3880, 0.421], // mobile assembled
  [4580, 0.4815], // mobile settle; signage fade begins
  [6082, 0.597], // signage assembled
  [6632, 0.63], // signage settle; email fade begins
  [8283, 0.757], // email assembled
  [8833, 0.7895], // email settle; report fade begins
  [11102, 0.964], // report assembled
  [11602, 1.0], // report settle before the loop hold
];
const SWEEP_TOTAL = SWEEP_PACE[SWEEP_PACE.length - 1][0];

const paceProgress = (e: number): number => {
  if (e <= 0) {
    return 0;
  }
  if (e >= SWEEP_TOTAL) {
    return 1;
  }
  for (let i = 1; i < SWEEP_PACE.length; i += 1) {
    const [t1, p1] = SWEEP_PACE[i];
    if (e <= t1) {
      const [t0, p0] = SWEEP_PACE[i - 1];
      return p0 + ((p1 - p0) * (e - t0)) / (t1 - t0);
    }
  }
  return 1;
};

/**
 * V2-only form-factor morph (Stage 4D). A subtle scale gesture folded into the
 * scene's depth scale so one product reads as changing surface: the outgoing
 * scene recedes and the incoming one emerges across the shared cross-fade band.
 *
 * It is a pure function of `p` and exactly 1 through every hold, so settled
 * states are byte-identical to Stage 4C. It multiplies `sceneScale` (the Stage
 * 2C depth scale) rather than replacing it, and only the automatic controller
 * applies it, so V1 is untouched and nothing double-scales. Bands are read from
 * PHASES, never modified.
 */
const MORPH_IN: Partial<Record<SceneId, number>> = {
  mobile: 0.06, // emerges as the smaller portrait form factor
  signage: 0.06, // opens into the wider surface
  email: 0.06,
  report: 0.06,
};
const MORPH_OUT: Partial<Record<SceneId, number>> = {
  desktop: 0.06, // recedes into the phone
  mobile: 0.06, // recedes as signage opens
  signage: 0.06,
  email: 0.06,
};

const sceneMorph = (id: SceneId, p: number): number => {
  const { fadeIn, fadeOut } = PHASES[id];
  let m = 1;
  const dIn = MORPH_IN[id];
  if (dIn && fadeIn) {
    m *= lerp(1 - dIn, 1, easeInOutCubic(segment(p, fadeIn[0], fadeIn[1])));
  }
  const dOut = MORPH_OUT[id];
  if (dOut && fadeOut) {
    m *= lerp(1, 1 - dOut, easeInOutCubic(segment(p, fadeOut[0], fadeOut[1])));
  }
  return m;
};

/*
 * Stage 4E soft auto-alignment (V2 only). The showcase changes scene on its own
 * clock while the page scrolls normally, so a scene can arrive badly framed for
 * wherever the reader happened to stop — title clipped under the header, or the
 * canvas bottom below the fold. When the reader is idle, the engine may nudge
 * the document by the minimum amount to frame the active scene well. It is a
 * calm settle, never scroll-jacking: gated to mouse-driven landscape (where the
 * scenes are taller than the safe zone), suppressed while the reader scrolls,
 * disabled off screen and under reduced motion, capped small, and skipped when a
 * large move would be needed. Full title always wins over full canvas.
 */
const ALIGN_MQ = '(min-width: 1024px) and (pointer: fine)';
const ALIGN_SAFE_TOP = 24; // clearance below the overlay header
const ALIGN_SAFE_BOTTOM = 30; // clearance above the viewport bottom
const ALIGN_DEADZONE = 10; // ignore deficits smaller than this
// Reveal (scroll up to un-clip a title) moves content down, away from the next
// section, so it is always safe and may travel a little further; tighten (scroll
// down to un-clip a canvas bottom) moves toward the next section, so it is kept
// small so it can never yank a reader who is really still on the intro.
const ALIGN_MAX_UP = 160;
const ALIGN_MAX_DOWN = 70;
const ALIGN_QUIET_MS = 700; // idle window required after the last manual scroll input
// Land the composition a few px higher than the bare safe line, so the bottom
// edge of the intro CTA never peeks above the showcase once framed.
const ALIGN_BIAS = 10;
// Eased settle for a correction: short, proportional to distance, never a snap.
const ALIGN_MIN_MS = 350;
const ALIGN_MAX_MS = 500;

class ShowcaseEngine {
  // Typing at its own cadence, then a short read pause, before the paced sweep.
  private static readonly PROMPT_MS = SEQUENCE_END + 1000;
  private static readonly SWEEP_MS = SWEEP_TOTAL;
  // Reports holds settled at the end of the sweep before the loop restarts.
  private static readonly HOLD_MS = 2000;
  // Opacity crossfade that hides the Reports -> Prompt swap at the loop seam; it
  // also eases the showcase in when a fresh cycle begins.
  private static readonly FADE_MS = 260;
  private static readonly SWEEP_END = ShowcaseEngine.PROMPT_MS + ShowcaseEngine.SWEEP_MS;
  private static readonly CYCLE_MS = ShowcaseEngine.SWEEP_END + ShowcaseEngine.HOLD_MS;
  // One full cycle including the closing fade-out; elapsed wraps back here.
  private static readonly LOOP_MS = ShowcaseEngine.CYCLE_MS + ShowcaseEngine.FADE_MS;
  // Enough of the showcase must be on screen before autoplay starts.
  private static readonly VISIBLE_RATIO = 0.35;

  private readonly root: HTMLElement;
  private readonly stage: HTMLElement;
  private readonly scenes: SceneRefs[];
  private readonly counters = new Map<string, CounterRef>();
  private readonly cadence: number[];
  private readonly header: HTMLElement | null;
  private readonly lastWritten = new Map<string, string>();
  private lastActive: SceneId | null = null;

  private elapsed = 0;
  private lastTs = 0;
  private raf = 0;
  private playing = false;
  private done = false;
  private inView = false;
  private engaged = false;
  private io?: IntersectionObserver;
  private headerIo?: IntersectionObserver;

  private readonly alignMq = window.matchMedia(ALIGN_MQ);
  private alignEnabled = this.alignMq.matches;
  private lastUserScroll = 0;
  private alignPending = false;
  private alignTarget: SceneId | null = null;
  private alignActive = false;
  private alignFrom = 0;
  private alignTo = 0;
  private alignStartTs = -1;
  private alignDur = 0;

  constructor(root: HTMLElement) {
    this.root = root;
    this.stage = root.querySelector<HTMLElement>('[data-stage]')!;
    this.scenes = SCENE_SEQUENCE.map((id) => ({
      id,
      el: root.querySelector<HTMLElement>(`[data-scene="${id}"]`)!,
      cues: FLAT_CUES.filter((cue) => cue.scene === id),
      props: [
        `--l-${id}`,
        ...FLAT_CUES.filter((cue) => cue.scene === id && cue.prop).map((cue) => cue.prop),
      ],
      live: false,
    })).filter((s) => s.el);

    const typed = root.querySelector<HTMLElement>('[data-layer-inner="typing"]');
    this.cadence = characterCadence(Number(typed?.dataset.length ?? 0));
    this.header = document.querySelector<HTMLElement>('header.site-header');

    for (const cue of FLAT_CUES) {
      if (!cue.countKey) {
        continue;
      }
      const el = root.querySelector<HTMLElement>(`[data-count="${cue.countKey}"]`);
      if (el) {
        this.counters.set(cue.countKey, { el, final: el.textContent ?? '' });
      }
    }
  }

  private write(name: string, value: string) {
    if (this.lastWritten.get(name) === value) {
      return;
    }
    this.lastWritten.set(name, value);
    this.stage.style.setProperty(name, value);
  }

  private clearScene(scene: SceneRefs) {
    if (!scene.live) {
      return;
    }
    scene.live = false;
    for (const name of scene.props) {
      this.stage.style.removeProperty(name);
      this.lastWritten.delete(name);
    }
    for (const cue of scene.cues) {
      const counter = cue.countKey ? this.counters.get(cue.countKey) : undefined;
      if (counter && counter.el.textContent !== counter.final) {
        counter.el.textContent = counter.final;
      }
    }
  }

  private renderInternals(p: number) {
    for (const scene of this.scenes) {
      if (sceneOpacity(scene.id, p) <= 0) {
        this.clearScene(scene);
        continue;
      }
      scene.live = true;
      const local = internalProgress(scene.id, p);
      this.write(`--l-${scene.id}`, local.toFixed(4));
      for (const cue of scene.cues) {
        if (cue.prop) {
          this.write(cue.prop, cueValue(cue, local).toFixed(3));
        }
        if (!cue.countKey) {
          continue;
        }
        const counter = this.counters.get(cue.countKey);
        const text = counterText(cue, local);
        if (counter && counter.el.textContent !== text) {
          counter.el.textContent = text;
        }
      }
    }
  }

  private applyTyping(state: TypingState) {
    this.write('--chars-prompt', `${state.chars}`);
    this.write('--caret-prompt', state.caret.toFixed(3));
    for (let i = 0; i < state.controls.length; i += 1) {
      this.write(`--r-prompt-controls-${i}`, state.controls[i].toFixed(3));
    }
    for (let i = 0; i < state.chips.length; i += 1) {
      this.write(`--r-prompt-chips-${i}`, state.chips[i].toFixed(3));
    }
    this.write('--r-prompt-more', state.more.toFixed(3));
    if (state.sentenceDone) {
      if (!this.root.dataset.hccTyped) {
        this.root.dataset.hccTyped = 'on';
      }
    } else if (this.root.dataset.hccTyped) {
      delete this.root.dataset.hccTyped;
    }
  }

  /** Progress from elapsed: held at 0 through the Prompt phase, then a paced sweep. */
  private progress(elapsed: number): number {
    return elapsed < ShowcaseEngine.PROMPT_MS
      ? 0
      : paceProgress(elapsed - ShowcaseEngine.PROMPT_MS);
  }

  /** Stage opacity: eases in at the cycle start, out across the loop seam. */
  private stageFade(elapsed: number): number {
    if (elapsed < ShowcaseEngine.FADE_MS) {
      return elapsed / ShowcaseEngine.FADE_MS;
    }
    if (elapsed >= ShowcaseEngine.CYCLE_MS) {
      return Math.max(0, 1 - (elapsed - ShowcaseEngine.CYCLE_MS) / ShowcaseEngine.FADE_MS);
    }
    return 1;
  }

  /** Wipe every per-cycle trace so the restarted loop never shows a stale frame. */
  private resetCycle() {
    for (const scene of this.scenes) {
      this.clearScene(scene);
    }
    this.lastActive = null;
    delete this.root.dataset.hccTyped;
    this.lastWritten.clear();
  }

  private render(elapsed: number) {
    const p = this.progress(elapsed);
    this.write('--p', p.toFixed(4));
    this.write('--hcc-showcase-fade', this.stageFade(elapsed).toFixed(3));

    for (const { id } of this.scenes) {
      this.write(`--o-${id}`, sceneOpacity(id, p).toFixed(3));
      this.write(`--y-${id}`, sceneTravel(id, p).toFixed(3));
      // The Stage 4D morph composes with the depth scale; identity through holds.
      this.write(`--s-${id}`, (sceneScale(id, p) * sceneMorph(id, p)).toFixed(4));
      this.write(`--to-${id}`, titleOpacity(id, p).toFixed(3));
      this.write(`--ty-${id}`, titleTravel(id, p).toFixed(3));
      this.write(`--ab-${id}`, abState(id, p).toFixed(3));
      this.write(`--q-${id}`, sceneQuiet(id, p).toFixed(3));
    }

    this.renderInternals(p);

    if (this.cadence.length > 0) {
      this.applyTyping(typingState(elapsed, this.cadence));
    }

    const active = dominantScene(p);
    if (active !== this.lastActive) {
      this.lastActive = active;
      for (const { id, el } of this.scenes) {
        const isActive = id === active;
        el.toggleAttribute('data-scene-active', isActive);
        el.inert = !isActive;
        el.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      }
      // Arm one framing check for the incoming surface scene (never the Prompt).
      this.alignPending = active !== 'prompt';
      this.alignTarget = active;
    }
    // Fire it once the scene is fully settled (opaque, morph resolved), so the
    // measured rect is the composition the reader will hold on.
    if (this.alignPending && this.alignTarget && sceneOpacity(this.alignTarget, p) >= 0.99) {
      if (this.tryAlign(this.alignTarget)) {
        this.alignPending = false;
      }
    }
  }

  private onUserScroll = () => {
    this.lastUserScroll = Date.now();
    // A manual scroll wins outright: stop any in-flight settle and do not resume
    // it for this activation.
    this.alignActive = false;
  };

  private onKeyScroll = (e: KeyboardEvent) => {
    if (
      e.key === 'ArrowUp' ||
      e.key === 'ArrowDown' ||
      e.key === 'PageUp' ||
      e.key === 'PageDown' ||
      e.key === 'Home' ||
      e.key === 'End' ||
      e.key === ' '
    ) {
      this.onUserScroll();
    }
  };

  private onAlignMq = (e: MediaQueryListEvent) => {
    this.alignEnabled = e.matches;
  };

  /**
   * One guarded framing correction for a settled surface scene. Returns true when
   * the check is resolved (a nudge was issued, none was needed, or the required
   * move was too large to apply); false while it must wait — the reader is
   * mid-scroll or the showcase is off screen — so the caller retries next frame.
   */
  private tryAlign(id: SceneId): boolean {
    if (!this.alignEnabled || window.matchMedia(REDUCED_MOTION).matches) {
      return true;
    }
    if (!this.inView) {
      return false;
    }
    if (Date.now() - this.lastUserScroll < ALIGN_QUIET_MS) {
      return false;
    }
    const scene = this.scenes.find((s) => s.id === id);
    const title = scene?.el.querySelector<HTMLElement>('[class*="sceneTitle"]');
    const canvas = scene?.el.querySelector<HTMLElement>('[class*="canvas"]');
    if (!title || !canvas) {
      return true;
    }
    const headerBottom = this.header ? this.header.getBoundingClientRect().bottom : 0;
    const safeTop = headerBottom + ALIGN_SAFE_TOP;
    const safeBottom = window.innerHeight - ALIGN_SAFE_BOTTOM;
    // When alignment happens is unchanged (clip is still judged against the safe
    // line); only the resting position is biased a few px higher.
    const restTop = safeTop - ALIGN_BIAS;
    const tr = title.getBoundingClientRect();
    const cr = canvas.getBoundingClientRect();
    let move = 0;
    let cap = 0;
    if (tr.top < safeTop - ALIGN_DEADZONE) {
      // Title clipped under the header: reveal it by lifting its top to restTop.
      move = tr.top - restTop;
      cap = ALIGN_MAX_UP;
    } else if (cr.bottom > safeBottom + ALIGN_DEADZONE) {
      // Canvas bottom clipped while the title has slack: tighten, but never past
      // the point where the title itself would clip — the full title always wins.
      move = Math.min(cr.bottom - safeBottom, Math.max(0, tr.top - restTop));
      cap = ALIGN_MAX_DOWN;
    }
    move = Math.round(move);
    if (Math.abs(move) < ALIGN_DEADZONE || Math.abs(move) > cap) {
      return true;
    }
    // Start an eased settle driven by the engine's own rAF (stepAlign in tick):
    // one clock, no second loop. A manual scroll cancels it (onUserScroll/pause).
    this.alignFrom = window.scrollY;
    this.alignTo = window.scrollY + move;
    this.alignDur = Math.min(ALIGN_MAX_MS, Math.max(ALIGN_MIN_MS, 300 + Math.abs(move) * 1.2));
    this.alignStartTs = -1;
    this.alignActive = true;
    return true;
  }

  /** Advance the eased scroll settle on the engine clock; no separate loop. */
  private stepAlign(ts: number) {
    if (!this.alignActive) {
      return;
    }
    if (this.alignStartTs < 0) {
      this.alignStartTs = ts;
    }
    const t = Math.min(1, (ts - this.alignStartTs) / this.alignDur);
    // `behavior: instant` bypasses the theme's `html { scroll-behavior: smooth }`,
    // so each frame lands exactly on this easing curve rather than being re-smoothed.
    window.scrollTo({
      top: this.alignFrom + (this.alignTo - this.alignFrom) * easeInOutCubic(t),
      behavior: 'instant',
    });
    if (t >= 1) {
      this.alignActive = false;
    }
  }

  private tick = (ts: number) => {
    if (!this.playing) {
      return;
    }
    // Clamp the delta so a throttled or backgrounded frame cannot jump the story.
    const dt = this.lastTs ? Math.min(ts - this.lastTs, 100) : 0;
    this.lastTs = ts;
    this.elapsed += dt;
    // Wrap at the loop seam, carrying the remainder so the cadence never drifts.
    if (this.elapsed >= ShowcaseEngine.LOOP_MS) {
      this.elapsed -= ShowcaseEngine.LOOP_MS;
      this.resetCycle();
    }
    this.render(this.elapsed);
    this.stepAlign(ts);
    this.raf = requestAnimationFrame(this.tick);
  };

  private play() {
    if (this.done || this.playing) {
      return;
    }
    this.playing = true;
    this.lastTs = 0;
    this.raf = requestAnimationFrame(this.tick);
  }

  private pause() {
    if (!this.playing) {
      return;
    }
    this.playing = false;
    // The settle rides this loop, so end it here rather than let it resume with a
    // stale timestamp (which would snap) when the loop restarts.
    this.alignActive = false;
    cancelAnimationFrame(this.raf);
  }

  private sync = () => {
    if (this.inView && !document.hidden && !this.done) {
      this.play();
    } else {
      this.pause();
    }
  };

  /** Header flow height, for the overlay compensation, and the top chrome line. */
  private measure(): number {
    const headerH = this.header ? this.header.offsetHeight : 0;
    const adminBar = parseFloat(getComputedStyle(document.documentElement).marginTop) || 0;
    const style = document.documentElement.style;
    style.setProperty('--hcc-header-h', `${headerH}px`);
    style.setProperty('--hcc-chrome-offset', `${adminBar + headerH}px`);
    return adminBar + headerH;
  }

  private onResize = () => {
    this.measure();
    this.render(this.elapsed);
    // A resize or orientation change re-frames the active scene, so allow one
    // fresh correction for it (never the Prompt).
    if (this.lastActive && this.lastActive !== 'prompt') {
      this.alignPending = true;
      this.alignTarget = this.lastActive;
    }
  };

  /** Play, or render a single settled poster under reduced motion. */
  private applyMotion = () => {
    if (window.matchMedia(REDUCED_MOTION).matches) {
      this.done = true;
      this.pause();
      // Reports settled: the most complete single product view. A static poster,
      // held at the sweep end so it is fully opaque and never loops.
      this.render(ShowcaseEngine.SWEEP_END);
    } else {
      this.done = false;
      this.elapsed = 0;
      this.render(0);
      this.sync();
    }
  };

  engage() {
    if (this.engaged) {
      return;
    }
    this.engaged = true;

    // Reuse the scroll engine's pinned-scene choreography; the variant stylesheet
    // turns the sticky track into a normal-flow showcase. 'on' also arms the
    // overlay header compensation (which keys on the attribute's presence).
    document.documentElement.dataset.hccJs = 'on';
    // No restore step here, so mark ready directly to reveal the static CTA.
    this.root.dataset.hccBoot = 'ready';

    const chrome = this.measure();

    // Play only while enough of the showcase is on screen.
    this.io = new IntersectionObserver(
      (entries) => {
        this.inView = entries.some(
          (e) => e.isIntersecting && e.intersectionRatio >= ShowcaseEngine.VISIBLE_RATIO,
        );
        this.sync();
      },
      { threshold: [0, ShowcaseEngine.VISIBLE_RATIO, 0.6] },
    );
    this.io.observe(this.stage);

    // Overlay header while the hero is in view; standard once it is fully passed.
    this.headerIo = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          delete document.documentElement.dataset.hccHeader;
        } else {
          document.documentElement.dataset.hccHeader = 'standard';
        }
      },
      { rootMargin: `-${Math.round(chrome)}px 0px 0px 0px`, threshold: 0 },
    );
    this.headerIo.observe(this.root);

    document.addEventListener('visibilitychange', this.sync);
    window.addEventListener('resize', this.onResize);

    // Manual-scroll intent, so a correction never fights the reader. Only these
    // inputs count as intent; the engine's own scrollTo does not fire them.
    window.addEventListener('wheel', this.onUserScroll, { passive: true });
    window.addEventListener('touchstart', this.onUserScroll, { passive: true });
    window.addEventListener('touchmove', this.onUserScroll, { passive: true });
    window.addEventListener('keydown', this.onKeyScroll);
    this.alignMq.addEventListener('change', this.onAlignMq);

    this.applyMotion();
  }

  reducedMotionChange = () => {
    this.applyMotion();
  };
}

function init() {
  const roots = document.querySelectorAll<HTMLElement>('[data-hcc-mode]');

  for (const root of roots) {
    // Showcase V4 renders its own stage (no [data-track]), so it forks first.
    if (root.dataset.hccVariant === 'showcase-v4') {
      if (!root.querySelector('[data-v4-stage]')) {
        continue;
      }
      const v4 = new ShowcaseV4Engine(root);
      const reducedV4 = window.matchMedia(REDUCED_MOTION);
      v4.engage();
      reducedV4.addEventListener('change', v4.reducedMotionChange);
      mountV4MobileStage(root);
      continue;
    }

    // Showcase V3 renders its own stage (no [data-track]), so it forks first.
    if (root.dataset.hccVariant === 'showcase-v3') {
      if (!root.querySelector('[data-v3-stage]')) {
        continue;
      }
      const v3 = new ShowcaseV3Engine(root);
      const reducedV3 = window.matchMedia(REDUCED_MOTION);
      v3.engage();
      reducedV3.addEventListener('change', v3.reducedMotionChange);
      continue;
    }

    if (!root.querySelector('[data-track]') || !root.querySelector('[data-stage]')) {
      continue;
    }

    // Automatic showcase (V2) runs its own time-driven controller; the scroll
    // engine (and its sticky track) must not run for it.
    if (root.dataset.hccVariant === 'automatic') {
      root.dataset.hccShowcase = 'on';
      const showcase = new ShowcaseEngine(root);
      const reducedM = window.matchMedia(REDUCED_MOTION);
      showcase.engage();
      reducedM.addEventListener('change', showcase.reducedMotionChange);
      continue;
    }

    const engine = new HeroEngine(root);
    const reduced = window.matchMedia(REDUCED_MOTION);

    const sync = () => (reduced.matches ? engine.release() : engine.engage());

    sync();
    reduced.addEventListener('change', sync);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
